import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { 
  calculateMonthlyPayment, 
  calculateTotalInterest, 
  calculateTotalRepaid, 
  generateAmortizationSchedule,
  calculateAPR,
  calculateDailyInterestRate,
  calculateDailyInterestAmount,
  convertMonthlyToAnnualRate,
  convertAnnualToMonthlyRate
} from '../../utils/calculations';
import * as Sentry from '@sentry/browser';

const StandardCalculator = ({ onCalculate }) => {
  const [isCalculating, setIsCalculating] = useState(false);
  const [rateInputMode, setRateInputMode] = useState('monthly'); // 'monthly' or 'annual'
  
  const { register, handleSubmit, formState: { errors }, watch } = useForm({
    defaultValues: {
      principal: 1000,
      monthlyInterestRate: 3.99,
      annualInterestRate: 41,
      termInMonths: 60,
      startDate: '',
      includeStartDate: false
    }
  });

  // Watch the form values for real-time calculations in display
  const watchedValues = watch();

  const onSubmit = async (data) => {
    try {
      setIsCalculating(true);
      console.log('Calculating standard repayment with:', data);
      
      // Parse form values
      const principal = parseFloat(data.principal);
      const termInMonths = parseInt(data.termInMonths, 10);
      
      // Determine which rate to use based on input mode
      let monthlyInterestRate, annualInterestRate;
      
      if (rateInputMode === 'monthly') {
        monthlyInterestRate = parseFloat(data.monthlyInterestRate);
        annualInterestRate = convertMonthlyToAnnualRate(monthlyInterestRate);
      } else {
        annualInterestRate = parseFloat(data.annualInterestRate);
        monthlyInterestRate = convertAnnualToMonthlyRate(annualInterestRate);
      }
      
      // Calculate key metrics
      const monthlyPayment = calculateMonthlyPayment(principal, annualInterestRate, termInMonths);
      const totalInterest = calculateTotalInterest(principal, annualInterestRate, termInMonths);
      const totalRepaid = calculateTotalRepaid(principal, annualInterestRate, termInMonths);
      
      // Calculate APR (for standard loans without fees, APR equals the annual interest rate)
      const apr = calculateAPR(principal, annualInterestRate, termInMonths);
      
      // Calculate daily interest rate and amount
      const dailyInterestRate = calculateDailyInterestRate(annualInterestRate);
      const dailyInterestAmount = calculateDailyInterestAmount(principal, annualInterestRate);
      
      // Generate amortization schedule
      const schedule = generateAmortizationSchedule(principal, annualInterestRate, termInMonths);
      
      // Add date information if provided
      let dateInfo = null;
      if (data.includeStartDate && data.startDate) {
        try {
          const startDate = new Date(data.startDate);
          if (!isNaN(startDate.getTime())) {
            dateInfo = {
              startDate: data.startDate,
              endDate: new Date(startDate.getFullYear(), startDate.getMonth() + termInMonths, startDate.getDate()).toISOString().split('T')[0],
              firstPaymentDate: new Date(startDate.getFullYear(), startDate.getMonth() + 1, startDate.getDate()).toISOString().split('T')[0]
            };
          }
        } catch (error) {
          console.warn('Invalid start date provided:', error);
        }
      }
      
      // Prepare results
      const results = {
        calculatorType: 'standard',
        principal,
        monthlyInterestRate,
        annualInterestRate,
        apr,
        dailyInterestRate,
        dailyInterestAmount,
        termInMonths,
        monthlyPayment,
        totalInterest,
        totalRepaid,
        schedule,
        rateInputMode, // Include the input mode for display purposes
        dateInfo // Include date information if provided
      };
      
      console.log('Standard calculation results:', results);
      
      // Pass results to parent component
      onCalculate(results);
    } catch (error) {
      console.error('Error calculating standard repayment:', error);
      
      // Capture the error in Sentry with additional context
      Sentry.captureException(error, {
        tags: {
          component: 'StandardCalculator',
          calculatorType: 'standard'
        },
        extra: {
          formData: data,
          principal: data.principal,
          monthlyInterestRate: data.monthlyInterestRate,
          annualInterestRate: data.annualInterestRate,
          termInMonths: data.termInMonths,
          rateInputMode
        }
      });
      
      alert('An error occurred during calculation. Please check your input values.');
    } finally {
      setIsCalculating(false);
    }
  };

  // Calculate derived rates for display
  const calculateDisplayRates = () => {
    if (rateInputMode === 'monthly') {
      if (!watchedValues.monthlyInterestRate || !watchedValues.principal) {
        return { annualRate: 0, aprRate: 0, dailyRate: 0, dailyAmount: 0 };
      }
      const monthlyRate = parseFloat(watchedValues.monthlyInterestRate) || 0;
      const principal = parseFloat(watchedValues.principal) || 0;
      const annualRate = convertMonthlyToAnnualRate(monthlyRate);
      const apr = calculateAPR(principal, annualRate, 1); // Use actual APR calculation
      
      return {
        annualRate,
        aprRate: apr.toNumber(),
        dailyRate: annualRate / 365,
        dailyAmount: (principal * annualRate / 100) / 365
      };
    } else {
      if (!watchedValues.annualInterestRate || !watchedValues.principal) {
        return { annualRate: 0, aprRate: 0, dailyRate: 0, dailyAmount: 0 };
      }
      const annualRate = parseFloat(watchedValues.annualInterestRate) || 0;
      const principal = parseFloat(watchedValues.principal) || 0;
      const apr = calculateAPR(principal, annualRate, 1); // Use actual APR calculation
      
      return {
        annualRate,
        aprRate: apr.toNumber(),
        dailyRate: annualRate / 365,
        dailyAmount: (principal * annualRate / 100) / 365
      };
    }
  };

  const displayRates = calculateDisplayRates();

  return (
    <div className="card">
      <h2 className="section-title">Standard Repayment Calculator</h2>
      <p className="mb-4 text-gray-600">
        Calculate your monthly repayments based on principal amount, interest rate, and loan term.
      </p>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Principal Amount (£)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="1"
              step="0.01"
              {...register("principal", { 
                required: "Principal amount is required", 
                min: { value: 1, message: "Amount must be positive" },
                valueAsNumber: true
              })}
            />
            {errors.principal && (
              <p className="mt-1 text-sm text-red-600">{errors.principal.message}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Interest Rate Input Mode
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setRateInputMode('monthly')}
                className={`px-3 py-2 text-sm rounded-md border ${
                  rateInputMode === 'monthly' 
                    ? 'bg-blue-600 text-white border-blue-600' 
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Monthly %
              </button>
              <button
                type="button"
                onClick={() => setRateInputMode('annual')}
                className={`px-3 py-2 text-sm rounded-md border ${
                  rateInputMode === 'annual' 
                    ? 'bg-blue-600 text-white border-blue-600' 
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Annual %
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Loan Term (months)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="1"
              step="1"
              {...register("termInMonths", { 
                required: "Loan term is required",
                min: { value: 1, message: "Term must be at least 1 month" },
                valueAsNumber: true
              })}
            />
            {errors.termInMonths && (
              <p className="mt-1 text-sm text-red-600">{errors.termInMonths.message}</p>
            )}
          </div>
        </div>

        {/* Interest Rate Input - changes based on mode */}
        <div className="mb-6">
          {rateInputMode === 'monthly' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monthly Interest Rate (%)
              </label>
              <input
                type="number"
                className="form-input box-border w-full md:w-1/3"
                min="0"
                step="0.01"
                {...register("monthlyInterestRate", { 
                  required: "Monthly interest rate is required",
                  min: { value: 0, message: "Rate must be 0 or positive" },
                  valueAsNumber: true
                })}
              />
              {errors.monthlyInterestRate && (
                <p className="mt-1 text-sm text-red-600">{errors.monthlyInterestRate.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Annual equivalent: {displayRates.annualRate.toFixed(2)}%
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Annual Interest Rate (%) - "Per Annum"
              </label>
              <input
                type="number"
                className="form-input box-border w-full md:w-1/3"
                min="0"
                step="0.01"
                {...register("annualInterestRate", { 
                  required: "Annual interest rate is required",
                  min: { value: 0, message: "Rate must be 0 or positive" },
                  valueAsNumber: true
                })}
              />
              {errors.annualInterestRate && (
                <p className="mt-1 text-sm text-red-600">{errors.annualInterestRate.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Monthly equivalent: {convertAnnualToMonthlyRate(displayRates.annualRate).toFixed(4)}%
              </p>
            </div>
          )}
        </div>

        {/* Calculated Rates Display */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Interest Rate Analysis</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-600 font-medium">Stated Annual Rate</p>
              <p className="text-lg font-semibold text-blue-700">{displayRates.annualRate.toFixed(2)}%</p>
              <p className="text-xs text-gray-500">As input or calculated</p>
            </div>
            <div>
              <p className="text-gray-600 font-medium">APR (True Cost)</p>
              <p className="text-lg font-semibold text-green-700">{displayRates.aprRate.toFixed(2)}%</p>
              <p className="text-xs text-gray-500">Regulatory standard</p>
            </div>
            <div>
              <p className="text-gray-600 font-medium">Daily Rate (%)</p>
              <p className="text-lg font-semibold text-blue-700">{displayRates.dailyRate.toFixed(4)}%</p>
            </div>
            <div>
              <p className="text-gray-600 font-medium">Daily Interest (£)</p>
              <p className="text-lg font-semibold text-blue-700">£{displayRates.dailyAmount.toFixed(2)}</p>
            </div>
          </div>
        </div>
        
        {/* Optional Date Fields */}
        <div className="mb-6">
          <div className="flex items-center mb-3">
            <input
              type="checkbox"
              id="includeStartDate"
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              {...register("includeStartDate")}
            />
            <label htmlFor="includeStartDate" className="ml-2 text-sm font-medium text-gray-700">
              Include Loan Start Date (for precise tracking and date-based calculations)
            </label>
          </div>
          
          {watch('includeStartDate') && (
            <div className="mt-3 p-4 bg-gray-50 rounded-md border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Loan Start Date
              </label>
              <input
                type="date"
                className="form-input box-border w-full md:w-1/2"
                {...register("startDate")}
              />
              <p className="mt-1 text-xs text-gray-500">
                When provided, payment schedules will show actual payment dates. This helps track which months have different day counts (28-31 days) for more accurate interest calculations.
              </p>
            </div>
          )}
        </div>
        
        <div className="text-right">
          <button 
            type="submit" 
            className="btn btn-primary cursor-pointer"
            disabled={isCalculating}
          >
            {isCalculating ? 'Calculating...' : 'Calculate'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StandardCalculator;